// LeaseViewPage — /leases/:groupId[?version=]. The staff way to VIEW a lease.
//
//   Miles, 2026-09-24: "There needs to be a way to view a lease with the card
//   view at the top of the page from within the backend."
//
// Top to bottom: a compact header (address, residents, status, term — names,
// places and dates only, never an id), the version strip when there is more
// than one version, the Review summary card (the same component the sign page
// and the builder mount, from the version's FROZEN snapshot), then the lease
// itself inline — every page, on every device (PdfPages) — with Download as
// an extra.
//
// The version comes from the URL: switching a version navigates, and the page
// refetches, so the summary and the document always describe the same version.
// Without ?version= the server opens on the lease residents are signing or
// signed (routes/leaseView defaultLeaseVersionId).
//
// Nothing generated yet → a plain statement and a link to the lease builder,
// which already shows the draft's summary above its live preview. A second draft
// view here would be a second thing that can drift.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { OcEmpty, OcError, OcLoading, StatusChip } from '@/components/oc/bits';
import { ReviewSummaryCard, generatedVersionSummaryIntro } from '@/components/ReviewSummaryCard';
import { PdfPages } from '@/components/PdfPages';
import { ViewDocVersionStrip } from '@/components/viewDocs/ViewDocVersionStrip';
import { docStateTone } from '@/lib/viewDocs';
import {
  fetchLeaseDocument,
  getLeaseView,
  type LeaseViewResponse,
  type LeaseViewState,
} from '@/lib/leaseViewClient';

const ROW: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-2)',
  alignItems: 'center',
  marginTop: 'var(--space-3)',
};

/** 'yyyy-mm-dd' → 'Aug 1, 2026', read as a LOCAL date (a bare ISO date parsed
 *  with new Date() is UTC midnight and shows the day before in US time). */
export function fmtLeaseDate(ymd: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd ?? '');
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** The document block's kicker, by the selected version's state. */
function documentKicker(state: LeaseViewState | undefined): string {
  switch (state) {
    case 'executed':
      return 'The fully executed lease';
    case 'draft':
      return 'The lease as generated — not sent';
    case 'cancelled':
      return 'The lease — cancelled';
    case 'declined':
      return 'The lease — declined';
    default:
      return 'The lease — as it stands now';
  }
}

// ---------------------------------------------------------------------------
// The document itself
// ---------------------------------------------------------------------------

export function LeaseViewDocument({
  path,
  filename,
  kicker,
}: {
  path: string;
  filename: string;
  kicker: string;
}): React.JSX.Element {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setBlob(null);
    setBytes(null);
    setError(null);
    void (async () => {
      try {
        const b = await fetchLeaseDocument(path);
        const buf = await b.arrayBuffer();
        if (!live) return;
        setBlob(b);
        setBytes(buf);
      } catch (err) {
        if (!live) return;
        const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
        setError(body?.error ?? 'The lease could not be loaded right now.');
      }
    })();
    return () => {
      live = false;
    };
  }, [path, attempt]);

  // Download = the very bytes on screen, so what you save is what you read.
  const downloadUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(
    () => () => {
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    },
    [downloadUrl],
  );

  return (
    <section className="oc-panel" data-block="document">
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-3)',
        }}
      >
        <span className="oc-kicker">{kicker}</span>
        {downloadUrl ? (
          <a className="btn" data-variant="secondary" data-size="sm" href={downloadUrl} download={filename}>
            Download
          </a>
        ) : (
          <button type="button" className="btn" data-variant="secondary" data-size="sm" disabled>
            Download
          </button>
        )}
      </div>
      {error ? (
        <OcError>
          <div>{error}</div>
          <div style={{ marginTop: 'var(--space-3)' }}>
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              onClick={() => setAttempt((n) => n + 1)}
            >
              Try again
            </button>
          </div>
        </OcError>
      ) : bytes ? (
        <PdfPages
          data={bytes}
          title="Lease"
          onError={() => setError('This lease can’t be shown on the page — use Download to open it.')}
        />
      ) : (
        <OcLoading label="Loading the lease…" />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The page body (pure — the fetch lives in LeaseViewPage)
// ---------------------------------------------------------------------------

export function LeaseViewContent({
  model,
  onSelectVersion,
}: {
  model: LeaseViewResponse;
  onSelectVersion: (versionId: string) => void;
}): React.JSX.Element {
  const selected = model.versions.find((v) => v.versionId === model.selectedVersionId) ?? null;
  const header = model.header;
  const term =
    header?.termStart && header.termEnd
      ? `${fmtLeaseDate(header.termStart)} – ${fmtLeaseDate(header.termEnd)}`
      : '';

  const crumbs = (
    <nav className="crumbs" aria-label="Breadcrumb">
      <Link to="/sent-items">Sent Items</Link>
      <span className="crumb-sep" aria-hidden="true" />
      <span aria-current="page">Lease</span>
    </nav>
  );

  if (model.versions.length === 0 || !selected) {
    return (
      <div className="oc-page">
        {crumbs}
        <header className="oc-panel">
          <span className="oc-kicker">Lease</span>
          <h1 className="oc-page-title">Not generated yet</h1>
        </header>
        <OcEmpty>
          No lease has been generated for this household yet. The lease builder shows the draft’s
          review summary above its live preview.{' '}
          <Link to={model.builderPath}>Open the lease builder</Link>.
        </OcEmpty>
      </div>
    );
  }

  return (
    <div className="oc-page">
      {crumbs}

      {/* ---------- Header: where, who, standing, when — no ids ---------- */}
      <header className="oc-panel" data-section="lease-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <span className="oc-kicker">Lease</span>
          {header?.isRenewal && <span className="tag">Renewal</span>}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
            marginTop: 'var(--space-1)',
          }}
        >
          <h1 className="oc-page-title">{header?.propertyAddress ?? 'Lease'}</h1>
          <StatusChip tone={docStateTone(selected.state)}>{selected.stateLabel}</StatusChip>
        </div>
        {header?.residentNames && <div className="oc-meta">{header.residentNames}</div>}
        {term && <div className="oc-meta">Term: {term}</div>}
        <div style={ROW}>
          <Link className="btn" data-variant="secondary" data-size="sm" to={model.builderPath}>
            Open in the lease builder
          </Link>
        </div>
      </header>

      {/* ---------- Versions (refetch through the URL) ---------- */}
      {model.versions.length > 1 && (
        <ViewDocVersionStrip
          versions={model.versions.map((v) => ({
            id: v.versionId,
            label: v.label,
            state: v.state,
            stateLabel: v.stateLabel,
            createdAt: v.generatedAt,
            current: v.versionId === model.selectedVersionId,
          }))}
          selectedId={model.selectedVersionId}
          onSelect={onSelectVersion}
        />
      )}

      {/* ---------- The card view, at the top ---------- */}
      {model.summary ? (
        <ReviewSummaryCard
          summary={model.summary}
          intro={generatedVersionSummaryIntro(selected.generatedAt)}
        />
      ) : (
        <OcEmpty>The review summary couldn’t be computed for this version. The lease itself is below.</OcEmpty>
      )}

      {/* ---------- The lease itself ---------- */}
      {model.document ? (
        <LeaseViewDocument
          key={model.document.path}
          path={model.document.path}
          filename={model.document.filename}
          kicker={documentKicker(selected.state)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The route
// ---------------------------------------------------------------------------

function loadErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    if (err.status === 404) return body?.error ?? 'There is no lease for this household.';
    return body?.error ?? `Couldn’t load the lease (HTTP ${err.status}).`;
  }
  return 'Couldn’t load the lease.';
}

export function LeaseViewPage(): React.JSX.Element {
  const groupId = useParams().groupId!;
  const [searchParams, setSearchParams] = useSearchParams();
  const version = searchParams.get('version');
  const [model, setModel] = useState<LeaseViewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const liveRef = useRef(0);

  const load = useCallback(async () => {
    const token = ++liveRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await getLeaseView(groupId, version);
      if (liveRef.current !== token) return;
      setModel(res);
    } catch (err) {
      if (liveRef.current !== token) return;
      setError(loadErrorMessage(err));
    } finally {
      if (liveRef.current === token) setLoading(false);
    }
  }, [groupId, version]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !model) return <OcLoading label="Loading the lease…" />;
  if (error && !model) {
    return (
      <OcError>
        <div>{error}</div>
        <div style={ROW}>
          <Link className="btn" data-variant="secondary" data-size="sm" to="/sent-items">
            Back to Sent Items
          </Link>
          <button type="button" className="btn" data-variant="secondary" data-size="sm" onClick={() => void load()}>
            Retry
          </button>
        </div>
      </OcError>
    );
  }
  if (!model) return <OcEmpty>Nothing to show.</OcEmpty>;

  return (
    <LeaseViewContent
      model={model}
      onSelectVersion={(versionId) =>
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('version', versionId);
          return next;
        })
      }
    />
  );
}
